import { Injectable, OnDestroy, inject } from "@angular/core";
import { isNil } from "lodash";
import { Observable, Subject, merge } from "rxjs";
import {
    filter,
    map,
    shareReplay,
    switchMap,
    tap
} from "rxjs/operators";
import { Mattslag, VirkeskopAvverkningskostnadstyp } from "~app/common/models/constants";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { CreateAvverkningskostnaderCommand } from "../commands/avverkningskostnader/CreateAvverkningskostnaderCommand";
import { ReplaceAvverkningskostnaderCommand } from "../commands/avverkningskostnader/ReplaceAvverkningskostnaderCommand";
import { Avverkningskostnad, AvverkningskostnadAtgard, AvverkningskostnaderEvent } from "../models/avverkningskostnader/avverkningskostnader-models";
import { VirkeskopKontraktstyp } from "../models/kontrakt-models";
import { AvverkningskostnaderService } from "./avverkningskostnader.service";
import { KontraktCommandService } from "./kontrakt-command.service";
import { KontraktMediatorService } from "./kontrakt-mediator.service";

@Injectable()
export class AvverkningskostnaderMediatorService implements OnDestroy {
    readonly avverkningskostnader$: Observable<AvverkningskostnaderEvent>;
    protected defaultMattslag = Mattslag.M3F;

    private readonly updateKostnaderSrc = new Subject<AvverkningskostnaderEvent>();
    private readonly avverkningskostnaderService = inject(AvverkningskostnaderService);
    private readonly destroy$ = new Subject<void>();
    private readonly kontraktCommandService = inject(KontraktCommandService);
    private readonly kontraktMediatorService = inject(KontraktMediatorService);

    private readonly fetchKostnader$ = this.kontraktMediatorService.kontrakt$.pipe(
        map(k => k.id),
        filter(id => !isNil(id)),
        switchMap(kontraktId => this.avverkningskostnaderService.getAvverkningskostnader(kontraktId)),
        tap(ak => { this._avverkningskostnader = ak; }),
        map(ak => new AvverkningskostnaderEvent(ak))
    );

    private _avverkningskostnader: AvverkningskostnadAtgard[];

    constructor() {
        this.avverkningskostnader$ = merge(
            this.fetchKostnader$,
            this.updateKostnaderSrc
        ).pipe(
            shareReplay(1)
        );
    }

    create(atgardGUID: string, source?: any) {
        const kontraktGUID = this.kontraktMediatorService.kontrakt.guid;

        const ny = new AvverkningskostnadAtgard();
        this.getOrCreate().push(ny);
        ny.virkeskopsatgardGuid = atgardGUID;
        ny.mattslag = this.getDefaultMattslag();
        ny.avverkningskostnader = [];
        ny.avverkningskostnader.push(new Avverkningskostnad(VirkeskopAvverkningskostnadstyp.Skordning, 0, 0));
        ny.avverkningskostnader.push(new Avverkningskostnad(VirkeskopAvverkningskostnadstyp.Skotning, 0, 0));
        ny.avverkningskostnader.push(new Avverkningskostnad(VirkeskopAvverkningskostnadstyp.Drivning, 0, 0));
        ny.avverkningskostnader.push(new Avverkningskostnad(VirkeskopAvverkningskostnadstyp.VagarAvlagg, 0, 0));
        ny.avverkningskostnader.push(new Avverkningskostnad(VirkeskopAvverkningskostnadstyp.Flytt, 0, 0));
        ny.avverkningskostnader.push(new Avverkningskostnad(VirkeskopAvverkningskostnadstyp.Ovrigt, 0, 0));
        this.kontraktCommandService.update(new CreateAvverkningskostnaderCommand(kontraktGUID, atgardGUID, ny.mattslag));
        this.notify(source);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    replaceAll(kontraktGUID: string, atgardGUID: string, avverkningskostnader: AvverkningskostnadAtgard, source?: any): void {
        this.kontraktCommandService.update(new ReplaceAvverkningskostnaderCommand(kontraktGUID, atgardGUID, avverkningskostnader));
        this.notify(source);
    }

    update(atgardGUID: string, mattslag: string, avverkningskostnad?: Avverkningskostnad, source?: any) {
        const kostnad = this._avverkningskostnader.find(x => x.virkeskopsatgardGuid === atgardGUID);
        kostnad.mattslag = mattslag;
        if (!isNil(avverkningskostnad)) {
            const radIndex = kostnad.avverkningskostnader.findIndex(x => x.typ === avverkningskostnad.typ);
            const modified = new Avverkningskostnad(
                avverkningskostnad.typ,
                avverkningskostnad.kostnadMattslag,
                avverkningskostnad.kostnadTotal,
                avverkningskostnad.kostnadBeraknadTotal,
                avverkningskostnad.id);
            kostnad.avverkningskostnader.splice(radIndex, 1, modified);
        }

        this.replaceAll(this.kontraktMediatorService.kontrakt.guid, atgardGUID, kostnad, source);
    }

    private getDefaultMattslag(): string {
        return (this.kontraktMediatorService.kontrakt.kontraktTyp === VirkeskopKontraktstyp.Grot) ? Mattslag.MWH : this.defaultMattslag;
    }

    private getOrCreate(): AvverkningskostnadAtgard[] {
        if (ObjectUtils.isNullOrUndefined(this._avverkningskostnader)) {
            this._avverkningskostnader = [];
        }
        return this._avverkningskostnader;
    }

    private notify(source?: any): void {
        this.updateKostnaderSrc.next(new AvverkningskostnaderEvent(this._avverkningskostnader, source));
    }
}
